import { plural, t } from '@lingui/core/macro'
import { useLingui } from '@lingui/react/macro'
import { PaginationState, Row, SortingState } from '@tanstack/react-table'
import { Box } from 'designSystem/components/Box'
import { Button } from 'designSystem/components/Button'
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
} from 'designSystem/components/InputGroup'
import { GeckoPowerTable } from 'features/knowledgeDocumentViewer/components/table/GeckoPowerTable'
import { AssetTypeFilter } from 'features/knowledgeDocumentViewer/components/toolbar/AssetTypeFilter'
import type { Box as ViewerBox } from 'features/knowledgeDocumentViewer/components/viewer/shared/types'
import { useKnowledgeDocumentDataContext } from 'features/knowledgeDocumentViewer/contexts/KnowledgeDocumentDataContext'
import { useKnowledgeDocumentUXContext } from 'features/knowledgeDocumentViewer/contexts/KnowledgeDocumentUXContext'
import { useLinkedDocumentsNavigation } from 'features/knowledgeDocumentViewer/hooks/useLinkedDocumentsNavigation'
import {
  OcrResultWithPage,
  useOcrResults,
} from 'features/knowledgeDocumentViewer/hooks/useOcrResults'
import { useOffPageConnectorDocuments } from 'features/knowledgeDocumentViewer/hooks/useOffPageConnectorDocuments'
import { DocumentViewerAssetType } from 'features/knowledgeDocumentViewer/types/ocrViewer'
import { ocrResultMatchKey } from 'features/knowledgeDocumentViewer/utils/viewerOcrBoxId'
import { ChevronDown, ChevronUp, Search } from 'lucide-react'
import React, { useCallback, useEffect, useMemo, useState } from 'react'

import { FeedbackModal } from './FeedbackModal'
import { createOcrTableColumns, OcrTableData } from './OcrTableColumns'

const CONNECTED_ASSETS_PANEL_ID = 'connected-assets-panel'
const OCR_TABLE_CELL_PADDING = '0 0.15rem'

function connectedAssetsLabel(count: number): string {
  return t`${plural(count, { one: '# Connected Asset', other: '# Connected Assets' })}`
}

export type OcrTableProps = {
  handleZoomToBox?: (id: string) => void
}

export const OcrTable: React.FC<OcrTableProps> = ({ handleZoomToBox }) => {
  const { t } = useLingui()
  const {
    currentDocumentQuery,
    activeTabId,
    currentPageNumber,
    currentDocumentId,
    currentSelectedBoxIds,
    updateDocumentHistoryItem,
    hoverId,
    handleMouseEnter,
    handleMouseLeave,
    isGeckoUser,
  } = useKnowledgeDocumentUXContext()

  const { siteID, showDocumentOcrBoxes } = useKnowledgeDocumentDataContext()

  const [sorting, setSorting] = useState<SortingState>([
    { id: 'refs', desc: true },
    { id: 'text', desc: false },
  ])
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 50,
  })
  const [feedbackModalResult, setFeedbackModalResult] =
    useState<OcrTableData | null>(null)
  const [hoveredResultText, setHoveredResultText] = useState<
    string | undefined
  >()
  const [isExpanded, setIsExpanded] = useState(false)

  const {
    systemDocumentEntries: offPageConnectorDocuments,
    isLoading: isLoadingOffPageConnector,
  } = useOffPageConnectorDocuments(hoveredResultText, siteID)

  const handleResultHover = useCallback(
    (result: OcrTableData | null) => {
      setHoveredResultText(
        showDocumentOcrBoxes &&
          result?.type === DocumentViewerAssetType.OFF_PAGE_CONNECTOR
          ? result.text
          : undefined
      )
    },
    [showDocumentOcrBoxes]
  )

  const { filteredOcrResults } = useOcrResults()
  const { ocrResultRefsMap, navigateToLinkedDocuments, isLoadingRefs } =
    useLinkedDocumentsNavigation({
      offPageConnectorDocuments: showDocumentOcrBoxes
        ? offPageConnectorDocuments
        : undefined,
      isLoadingOffPageConnector: showDocumentOcrBoxes
        ? isLoadingOffPageConnector
        : false,
    })

  const ocrTableData = useMemo<OcrTableData[]>(
    () =>
      filteredOcrResults
        .filter((result) => isGeckoUser || result.confirmed)
        .map((result) => ({
          ...result,
          refsCount:
            ocrResultRefsMap.get(
              ocrResultMatchKey(result.id, result.pageNumber)
            ) ?? 0,
        })),
    [filteredOcrResults, isGeckoUser, ocrResultRefsMap]
  )

  const handleSearch = useCallback(
    (searchInput: string) => {
      updateDocumentHistoryItem(activeTabId, { query: searchInput })
    },
    [updateDocumentHistoryItem, activeTabId]
  )

  const handleToggleExpanded = useCallback(() => {
    setIsExpanded((current) => !current)
  }, [])

  const handleZoomToBoxClick = useCallback(
    (result: OcrTableData) => {
      if (!handleZoomToBox) return

      const needsNavigation =
        result.pageNumber !== currentPageNumber &&
        result.pageNumber !== currentPageNumber + 1

      if (needsNavigation) {
        updateDocumentHistoryItem(activeTabId, {
          pageNumber: result.pageNumber,
          selectedBoxIds: [],
        })
        setTimeout(() => {
          const boxId = `${result.id}-0`
          handleZoomToBox(boxId)
        }, 500)
        return
      }

      const pageIndex = result.pageNumber === currentPageNumber + 1 ? 1 : 0
      const boxId = `${result.id}-${pageIndex}`
      handleZoomToBox(boxId)
    },
    [handleZoomToBox, currentPageNumber, updateDocumentHistoryItem, activeTabId]
  )

  const handleFlagClick = useCallback((result: OcrTableData) => {
    setFeedbackModalResult(result)
  }, [])

  const getBoxIdForOcrResult = useCallback(
    (result: OcrResultWithPage): string => {
      const pageIndex = result.pageNumber === currentPageNumber ? 0 : 1
      return `${result.id}-${pageIndex}`
    },
    [currentPageNumber]
  )

  const handleViewLinkedClick = useCallback(
    (result: OcrTableData, event: React.MouseEvent) => {
      const forceNewTab = event.metaKey || event.ctrlKey

      const boxId = getBoxIdForOcrResult(result)
      updateDocumentHistoryItem(activeTabId, { selectedBoxIds: [boxId] })

      navigateToLinkedDocuments({ ocrResult: result, forceNewTab })
    },
    [
      activeTabId,
      getBoxIdForOcrResult,
      navigateToLinkedDocuments,
      updateDocumentHistoryItem,
    ]
  )

  const columns = useMemo(
    () =>
      createOcrTableColumns({
        isLoadingRefs,
        onZoomToBox: handleZoomToBoxClick,
        onFlagClick: handleFlagClick,
        onViewLinkedClick: handleViewLinkedClick,
        onResultHover: handleResultHover,
        canNavigateOffPageConnectors: showDocumentOcrBoxes,
      }),
    [
      isLoadingRefs,
      handleZoomToBoxClick,
      handleFlagClick,
      handleViewLinkedClick,
      handleResultHover,
      showDocumentOcrBoxes,
    ]
  )

  const createMockBox = useCallback(
    (result: OcrResultWithPage): ViewerBox => {
      const boxId = getBoxIdForOcrResult(result)
      return {
        id: boxId,
        label: result.text ?? '',
        type: result.type,
        confirmed: result.confirmed ?? false,
        x: 0,
        y: 0,
        w: 0,
        h: 0,
      }
    },
    [getBoxIdForOcrResult]
  )

  const handleRowClick = useCallback(
    (row: Row<OcrTableData>) => {
      const result = row.original
      const boxId = getBoxIdForOcrResult(result)

      updateDocumentHistoryItem(activeTabId, { selectedBoxIds: [boxId] })
      handleMouseEnter(createMockBox(result))
    },
    [
      activeTabId,
      getBoxIdForOcrResult,
      updateDocumentHistoryItem,
      handleMouseEnter,
      createMockBox,
    ]
  )

  const { highlightedRowId, highlightedRowIndex } = useMemo(() => {
    if (currentSelectedBoxIds.length === 0) {
      return { highlightedRowId: null, highlightedRowIndex: -1 }
    }

    const selectedBoxId = currentSelectedBoxIds[0]
    const matchingIndex = ocrTableData.findIndex((result) => {
      const boxId = getBoxIdForOcrResult(result)
      return boxId === selectedBoxId
    })

    if (matchingIndex !== -1) {
      const matchingResult = ocrTableData[matchingIndex]
      return {
        highlightedRowId: `${matchingResult.id}-${matchingResult.pageNumber}-${matchingIndex}`,
        highlightedRowIndex: matchingIndex,
      }
    }
    return { highlightedRowId: null, highlightedRowIndex: -1 }
  }, [currentSelectedBoxIds, ocrTableData, getBoxIdForOcrResult])

  const hoveredRowId = useMemo(() => {
    if (!hoverId) return null

    const matchingIndex = ocrTableData.findIndex((result) => {
      const boxId = getBoxIdForOcrResult(result)
      return boxId === hoverId
    })

    if (matchingIndex !== -1) {
      const matchingResult = ocrTableData[matchingIndex]
      return `${matchingResult.id}-${matchingResult.pageNumber}-${matchingIndex}`
    }
    return null
  }, [hoverId, ocrTableData, getBoxIdForOcrResult])

  const handleRowHoverStart = useCallback(
    (row: Row<OcrTableData>) => {
      handleMouseEnter(createMockBox(row.original))
    },
    [handleMouseEnter, createMockBox]
  )

  const handleRowHoverEnd = useCallback(() => {
    handleMouseLeave()
    if (currentSelectedBoxIds.length > 0) {
      const selectedBoxId = currentSelectedBoxIds[0]
      const matchingResult = ocrTableData.find((result) => {
        const boxId = getBoxIdForOcrResult(result)
        return boxId === selectedBoxId
      })
      if (matchingResult) {
        handleMouseEnter(createMockBox(matchingResult))
      }
    }
  }, [
    handleMouseLeave,
    handleMouseEnter,
    currentSelectedBoxIds,
    ocrTableData,
    getBoxIdForOcrResult,
    createMockBox,
  ])

  useEffect(() => {
    if (highlightedRowId && highlightedRowIndex !== -1) {
      const targetPageIndex = Math.floor(
        highlightedRowIndex / pagination.pageSize
      )

      if (targetPageIndex !== pagination.pageIndex) {
        setPagination((prev) => ({ ...prev, pageIndex: targetPageIndex }))
      }

      const timeoutId = setTimeout(() => {
        const rowElement = document.getElementById(highlightedRowId)
        if (rowElement) {
          rowElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
        }
      }, 100)
      return () => clearTimeout(timeoutId)
    }
  }, [
    highlightedRowId,
    highlightedRowIndex,
    pagination.pageSize,
    pagination.pageIndex,
  ])

  useEffect(() => {
    if (currentSelectedBoxIds.length === 0) {
      handleMouseLeave()
    }
  }, [currentSelectedBoxIds.length, handleMouseLeave])

  if (ocrTableData.length === 0) {
    return null
  }

  return (
    <Box
      display='flex'
      direction='column'
      flex={isExpanded ? 'one' : 'none'}
      minHeight='none'
      overflow='hidden'>
      <Box
        display='flex'
        direction='column'
        gap='sm'
        padding='sm'
        shrink={false}>
        <Button
          type='button'
          variant='outline'
          size='lg'
          width='full'
          aria-expanded={isExpanded}
          aria-controls={CONNECTED_ASSETS_PANEL_ID}
          onClick={handleToggleExpanded}>
          <Box
            asChild
            display='flex'
            align='center'
            justify='between'
            width='full'
            gap='sm'>
            <span>
              <span>{connectedAssetsLabel(ocrTableData.length)}</span>
              {isExpanded ? (
                <ChevronUp data-icon='inline-end' />
              ) : (
                <ChevronDown data-icon='inline-end' />
              )}
            </span>
          </Box>
        </Button>
        {isExpanded ? (
          <Box display='flex' align='center' gap='sm'>
            <Box flex='one' minWidth='none'>
              <InputGroup>
                <InputGroupInput
                  type='search'
                  placeholder={t`Search Tags`}
                  value={currentDocumentQuery}
                  onChange={(event) => handleSearch(event.target.value)}
                  aria-label={t`Search tags`}
                />
                <InputGroupAddon align='inline-start' aria-hidden='true'>
                  <Search />
                </InputGroupAddon>
              </InputGroup>
            </Box>
            <AssetTypeFilter />
          </Box>
        ) : null}
      </Box>
      <Box
        id={CONNECTED_ASSETS_PANEL_ID}
        display={isExpanded ? 'flex' : 'hidden'}
        direction='column'
        flex='one'
        minHeight='none'
        overflow='hidden'>
        <GeckoPowerTable
          data={ocrTableData}
          columns={columns}
          isLoadingData={false}
          footerName={t`Connected Assets`}
          pagination={pagination}
          setPagination={setPagination}
          sorting={sorting}
          setSorting={setSorting}
          showFilterBar={false}
          showTable={true}
          showFooter={true}
          cellPadding={OCR_TABLE_CELL_PADDING}
          cellFontSize='0.5rem'
          maxHeight='100%'
          getRowId={(row, index) => `${row.id}-${row.pageNumber}-${index}`}
          onRowClick={handleRowClick}
          highlightSelectedRow={true}
          controlledHighlightedRowId={highlightedRowId}
          onRowHoverStart={handleRowHoverStart}
          onRowHoverEnd={handleRowHoverEnd}
          controlledHoveredRowId={hoveredRowId}
        />
      </Box>
      {feedbackModalResult ? (
        <FeedbackModal
          feedbackType='asset'
          isOpen={feedbackModalResult !== null}
          onClose={() => setFeedbackModalResult(null)}
          boxId={String(feedbackModalResult.id)}
          boxLabel={feedbackModalResult.text ?? null}
          boxType={feedbackModalResult.type ?? null}
          documentId={currentDocumentId}
          siteId={siteID}
        />
      ) : null}
    </Box>
  )
}
